// Contract detail page.
//
// Chrome mirrors the Bunker enquiry form screen
// (``frontend/src/components/FuelEnquiries/FuelEnquiryForm.js``) via the
// shared ``.hms-form-screen`` rules in
// ``frontend/src/components/ui/HMSScreenHeader.css``:
//  - ``<Layout>`` shell with the left side nav
//  - ``.legal-screen`` blue gradient background (shared with the Legal
//    dashboard)
//  - ``.hms-screen-header`` with absolute ``.hms-back-circle`` on the
//    left and the action-button cluster on the right
//  - ``.hms-header-logo`` + ``.hms-header-logo-img`` for the white HMS
//    logo, then ``.hms-header-name-display`` for the contract number,
//    matching the bunker form's name treatment
//  - Global ``<Footer />`` at the bottom
//
// View-mode + edit-mode share the same chrome — only the inner field
// markup swaps between read-only displays and inputs. Save calls
// PATCH /api/legal/contracts/<id>/. Resend calls
// POST /api/legal/contracts/<id>/resend-email/.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faEnvelope,
  faExchangeAlt,
  faExclamationTriangle,
  faGavel,
  faLink,
  faPencil,
  faUpload,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import Section from '../../components/ui/Section';
import Field from '../../components/ui/Field';
import HMSButton from '../../components/ui/HMSButton';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import HMSFloatingActionMenu from '../../components/ui/HMSFloatingActionMenu';
import { DatePicker, DateRangePicker } from '../../components/ui';
import { CONTRACT_STATUS_OPTIONS, legalContractService } from '../../services/legalContractService';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import useContractReview, { byLine } from '../../components/Legal/useContractReview';
import useContractExpiry from '../../components/Legal/useContractExpiry';
import useAddedNotice from '../../components/Legal/useAddedNotice';
import { dealFieldsRequired, positionOptions } from '../../components/Legal/contractFields';
import { expiryFlag, expiryText, formatDay, noStepsText, stepLine } from '../../components/Legal/contractExpiry';
import HMSBadge from '../../components/ui/HMSBadge';
import ContractFileList from './components/ContractFileList';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/images/IMT_logo.png';

import '../../components/ui/HMSScreenHeader.css';
// Pulls in the shared ``.fef-next-step`` / ``.fef-next-step--info`` banner
// rules used on the Bunker enquiry + recap screens, so the contract
// detail "Paired Contract" / "Source Recap" banners look identical.
import '../../components/FuelEnquiries/FuelEnquiryForm.css';
import './LegalDashboard.css';
import './LegalContractDetail.css';

import PinToDeskButton from '../../components/mydesk/PinToDeskButton';
const CONTRACT_POSITION_OPTIONS = [
  { value: 'Co-', label: 'Co- (Commission Paid)' },
  { value: 'Co+', label: 'Co+ (Commission Earned)' },
  { value: 'Pr', label: 'Pr (Principal)' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (_e) {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return value;
  }
};

const formatPeriod = (start, end) => {
  if (!start && !end) return '—';
  return `${formatDate(start)} – ${formatDate(end)}`;
};

// Project a full contract record onto the slimmer edit-form shape.
// Anything not in this list is structurally immutable post-mint and is
// not surfaced as a writable input.
const toFormState = (contract) => ({
  entity: contract.entity ?? '',
  contract_position: contract.contract_position || '',
  commence_date: contract.commence_date || '',
  period_start: contract.period_start || '',
  period_end: contract.period_end || '',
  term_notes: contract.term_notes || '',
});

const LegalContractDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  // View Recap opens a Trade Ops screen: offer it only to someone that
  // route lets in (ProtectedRoute's hasTradeOpsAccess). A Legal Approver
  // with no trade module reads the recap number on the banner instead.
  const { user } = useAuth();
  const canOpenRecap = Boolean(
    user?.has_full_access || user?.is_superuser || user?.is_staff
    || user?.has_cargo_access || user?.has_bunkers_access,
  );
  // Forward the year selected on the Legal dashboard back through the
  // detail screen so the back button restores it. Mirrors how the
  // Trade Ops detail screens preserve ``location.state.selectedDate``.
  const backToLegalState = location.state?.selectedDate
    ? { selectedDate: location.state.selectedDate }
    : undefined;
  const goBackToLegal = () =>
    history.push({ pathname: '/dashboard/legal', state: backToLegalState });
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit-mode state
  const [isEditing, setIsEditing] = useState(false);
  const [formState, setFormState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  // Resend-email + flash feedback
  const [resending, setResending] = useState(false);
  // A contract just made with Add contract says so, once.
  const justAdded = useAddedNotice();
  const [flash, setFlash] = useState(() => (
    justAdded ? { kind: 'success', message: 'Contract added.' } : null
  )); // { kind: 'success' | 'error', message }

  // Imperative handle into ContractFileList so the floating action menu
  // can fire "Upload file" without the parent having to lift the
  // upload-state machinery out of the child.
  const fileListRef = useRef(null);
  const openUploadPicker = () => {
    fileListRef.current && fileListRef.current.openFilePicker();
  };

  // Status, Send to Legal and Mark reviewed (shared with the new look page).
  const review = useContractReview({ contract, onUpdated: setContract, onMessage: setFlash });
  // Expiry and reminders (components/Legal/useContractExpiry).
  const expiry = useContractExpiry({ contract, onUpdated: setContract, onMessage: setFlash });

  const refreshContract = useCallback(async () => {
    const data = await legalContractService.get(id);
    setContract(data);
    return data;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchContract = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await legalContractService.get(id);
        if (!cancelled) setContract(data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError('Contract not found.');
        } else {
          setError(
            err?.response?.data?.detail
              || err?.message
              || 'Failed to load contract.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchContract();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Auto-dismiss the flash after ~5s. Keep the timer in a ref-like
  // cleanup so navigating away doesn't fire setState on an unmounted
  // component.
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const enterEditMode = async () => {
    if (!contract) return;
    setEditError(null);
    setFormState(toFormState(contract));
    setIsEditing(true);
    // Lazily fetch entity options the first time the user enters edit.
    if (entities.length === 0) {
      setEntitiesLoading(true);
      try {
        const list = await fetchHMSEntities();
        setEntities(Array.isArray(list) ? list : []);
      } finally {
        setEntitiesLoading(false);
      }
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormState(null);
    setEditError(null);
  };

  const handleFieldChange = (key, value) => {
    setFormState((prev) => ({ ...(prev || {}), [key]: value }));
  };

  // DatePicker + DateRangePicker emit synthetic {target:{name,value}} events.
  const handleDateEvent = (e) => {
    if (!e?.target?.name) return;
    handleFieldChange(e.target.name, e.target.value);
  };

  const buildPatchPayload = () => {
    // Send all editable fields every PATCH. The backend serializer
    // ignores any field outside its allow-list, so this is the safest
    // shape (no risk of a partial update accidentally clearing one of
    // the period bounds because the user opened then closed the
    // picker).
    return {
      entity: formState.entity || null,
      contract_position: formState.contract_position,
      commence_date: formState.commence_date || null,
      period_start: formState.period_start || null,
      period_end: formState.period_end || null,
      term_notes: formState.term_notes || '',
    };
  };

  const saveEdit = async () => {
    if (!formState) return;
    setSaving(true);
    setEditError(null);
    try {
      const updated = await legalContractService.update(id, buildPatchPayload());
      setContract(updated);
      setIsEditing(false);
      setFormState(null);
      setFlash({ kind: 'success', message: 'Contract updated.' });
    } catch (err) {
      const data = err?.response?.data;
      // Surface field-level errors when available.
      let message = data?.detail || err?.message || 'Save failed.';
      if (data && typeof data === 'object') {
        const firstFieldErr = Object.entries(data).find(
          ([, v]) => Array.isArray(v) && v.length > 0,
        );
        if (firstFieldErr) {
          message = `${firstFieldErr[0]}: ${firstFieldErr[1][0]}`;
        }
      }
      setEditError(message);
    } finally {
      setSaving(false);
    }
  };

  const resendEmail = async () => {
    if (resending) return;
    setResending(true);
    try {
      const result = await legalContractService.resendEmail(id);
      if (result.sent) {
        setFlash({
          kind: 'success',
          message: `Counterparty email re-sent to ${result.recipient}.`,
        });
      } else if (result.recipient) {
        setFlash({
          kind: 'error',
          message: `Send failed (SMTP). Recipient was ${result.recipient}.`,
        });
      } else {
        setFlash({
          kind: 'error',
          message: 'No counterparty email on this enquiry — cannot send.',
        });
      }
    } catch (err) {
      setFlash({
        kind: 'error',
        message: err?.response?.data?.detail
          || err?.message
          || 'Resend failed.',
      });
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <Layout showLeftNavSidebar showRightSidebar={false}>
        <div className="legal-screen hms-form-screen legal-detail-screen">
          <div className="legal-detail-loading">
            <LoadingSpinner message="Loading contract..." size="medium" />
          </div>
          <Footer />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showLeftNavSidebar showRightSidebar={false}>
        <div className="legal-screen hms-form-screen legal-detail-screen">
          <div className="hms-screen-header">
            <button
              type="button"
              className="hms-back-circle"
              onClick={goBackToLegal}
              title="Back to Legal"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className="hms-header-logo">
              <img src={logo} alt="IMT" className="hms-header-logo-img" />
              <div className="hms-header-title-content">
                <div className="hms-header-name-row">
                  <FontAwesomeIcon icon={faGavel} style={{ color: 'white', marginRight: '6px' }} />
                  <span className="hms-header-name-display">Contract</span>
                </div>
              </div>
            </div>
          </div>
          <div className="legal-detail-error" role="alert">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
          <Footer />
        </div>
      </Layout>
    );
  }

  if (!contract) return null;

  const filesCount = Array.isArray(contract.files) ? contract.files.length : 0;
  const paired = contract.paired_contract_summary;
  const recapSummary = contract.trade_recap_summary;

  return (
    <Layout showLeftNavSidebar showRightSidebar={false}>
      <div className="legal-screen hms-form-screen legal-detail-screen">
        <div className="hms-screen-header">
          <button
            type="button"
            className="hms-back-circle"
            onClick={goBackToLegal}
            title="Back to Legal"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>

          <div className="hms-header-logo">
            <img src={logo} alt="IMT" className="hms-header-logo-img" />
            <div className="hms-header-title-content">
              <div className="hms-header-name-row">
                <FontAwesomeIcon icon={faGavel} style={{ color: 'white', marginRight: '6px' }} />
                <span className="hms-header-name-display legal-detail-cno">
                  {contract.contract_number}
                </span>
              </div>
              <div className="hms-header-info-row">
                <span className="hms-recap-no-badge">
                  {contract.contract_type_display || contract.contract_type}
                </span>
                {/* Paired contract + source recap are no longer inline
                  * meta-links here; they're rendered as full-width info
                  * banners below the header (same shape as the Bunker
                  * recap "Paired Sale Recap" banner). */}
              </div>
            </div>
          </div>

          {/* Read-mode actions live in the floating action menu (rendered
            * below the header so it pins to the viewport, not the header
            * row). Edit-mode keeps Cancel/Save inline because those two
            * are a "complete this transaction" pair, not a long-lived
            * options menu. */}
          <PinToDeskButton entityType="legal_contract" objectId={id} className="legal-detail-pin" />
          {isEditing && (
            <div className="hms-header-actions legal-detail-header-actions">
              <button
                type="button"
                className="hms-btn hms-btn-secondary"
                onClick={cancelEdit}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faXmark} />
                Cancel
              </button>
              <button
                type="button"
                className="hms-btn hms-btn-success"
                onClick={saveEdit}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faCheck} />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Write actions (upload / resend / edit) only for users the
          * backend lets mutate this contract. Scoped Bunkers operators get
          * a read-only view — the whole action menu is suppressed (download
          * still lives per-file in ContractFileList). */}
        {!isEditing && contract.can_edit && (
          <HMSFloatingActionMenu
            label="Actions"
            items={[
              {
                key: 'upload-file',
                label: 'Upload File',
                icon: faUpload,
                color: 'green',
                onClick: openUploadPicker,
                title: 'Upload a contract file (PDF, DOC, DOCX, PNG, JPG — max 25 MB)',
              },
              // Resend is a bunkers pair concept (one email covering the
              // Sale + Purchase legs). Freight, cargo and added contracts have
              // no paired leg, so the action is hidden rather than 400ing on click.
              ...(contract.commodity_type !== 'fuels' ? [] : [{
                key: 'resend-email',
                label: resending ? 'Sending…' : 'Resend Email',
                icon: faEnvelope,
                color: 'blue',
                onClick: resendEmail,
                disabled: resending,
                title: 'Re-send the HAMSTIR counterparty email for this contract pair',
              }]),
              {
                key: 'edit-contract',
                label: 'Edit',
                icon: faPencil,
                color: 'orange',
                onClick: enterEditMode,
                title: 'Edit contract terms',
              },
            ]}
          />
        )}

      {flash && (
        <div
          className={`legal-detail-flash legal-detail-flash--${flash.kind}`}
          role={flash.kind === 'error' ? 'alert' : 'status'}
        >
          {flash.message}
          <button
            type="button"
            className="legal-detail-flash__dismiss"
            onClick={() => setFlash(null)}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {editError && (
        <div className="legal-detail-error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{editError}</span>
        </div>
      )}

      {/* Relationship banner — same shape (.fef-next-step--info) as
        * the Bunker recap "Paired Sale/Purchase Recap" banner so the
        * trader sees one banner system across enquiries, recaps, and
        * contracts. ONE banner with every related-artifact button
        * stacked in the actions row (mirrors TradeRecap.js where
        * "View Enquiry", "View Sale/Purchase Recap" and "View Contract"
        * all live in a single banner). Only shown in read mode; during
        * edit the user is already focused on this contract's fields. */}
      {!isEditing && (paired || (recapSummary && recapSummary.id)) && (
        <div className="legal-detail-banners">
          <div className="fef-next-step fef-next-step--info" role="note">
            <FontAwesomeIcon
              icon={paired ? faExchangeAlt : faLink}
              className="fef-next-step-icon"
            />
            <div className="fef-next-step-body">
              <div className="fef-next-step-title">
                {paired ? 'Paired Contract' : 'Source Recap'}
              </div>
              <div className="fef-next-step-desc">
                {paired
                  ? paired.contract_number
                  : (recapSummary.transaction_number || `#${recapSummary.id}`)}
              </div>
            </div>
            <div className="fef-next-step-actions">
              {paired && (
                <button
                  type="button"
                  className="fef-next-step-action"
                  onClick={() => history.push(
                    `/dashboard/legal/contracts/${paired.id}`,
                  )}
                >
                  View Contract{' '}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
              {recapSummary && recapSummary.id && canOpenRecap && (
                <button
                  type="button"
                  className="fef-next-step-action"
                  onClick={() => history.push(
                    `/trade-recap/${recapSummary.id}`,
                    { commodityType: contract.commodity_type === 'solids' ? 'solids' : 'fuels' },
                  )}
                >
                  View Recap{' '}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="legal-detail-grid">
        <div className="legal-detail-grid__main">
          <Section title="Contract Details" stepNumber={1}>
            <div className="hms-grid hms-grid-2">
              <Field label="Entity" required={isEditing}>
                {isEditing ? (
                  <select
                    value={formState.entity ?? ''}
                    onChange={(e) => handleFieldChange(
                      'entity',
                      e.target.value ? Number(e.target.value) : '',
                    )}
                    disabled={saving || entitiesLoading}
                  >
                    <option value="">
                      {entitiesLoading ? 'Loading entities…' : 'Select entity…'}
                    </option>
                    {/* Row 128: the company list is {value: HMSEntity id, label: name}
                        (crm get_hms_entities); reading id / hms_entity_name left
                        every option blank. contract.entity is that same id. */}
                    {entities.map((entity) => (
                      <option key={entity.value} value={entity.value}>
                        {entity.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="hms-input-readonly">
                    {contract.entity_name || '—'}
                  </div>
                )}
              </Field>

              <Field label="Counterparty">
                <div className="hms-input-readonly">
                  {contract.counterparty_name || '—'}
                  {contract.counterparty_in_crm === false && (
                    <span className="legal-detail-not-in-crm"> (not in CRM)</span>
                  )}
                </div>
              </Field>

              <Field label="Contract Type" hint={isEditing ? 'Set at mint time' : undefined}>
                <div className="hms-input-readonly">
                  {contract.contract_type_display || contract.contract_type}
                </div>
              </Field>

              <Field label="Contract Position" required={isEditing && dealFieldsRequired(contract)}>
                {isEditing ? (
                  <select
                    value={formState.contract_position || ''}
                    onChange={(e) => handleFieldChange('contract_position', e.target.value)}
                    disabled={saving}
                  >
                    {positionOptions(contract, CONTRACT_POSITION_OPTIONS).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="hms-input-readonly">
                    {contract.contract_position_display
                      || contract.contract_position
                      || '—'}
                  </div>
                )}
              </Field>

              <Field label="Commence Date" required={isEditing && dealFieldsRequired(contract)}>
                {isEditing ? (
                  <DatePicker
                    id="legal-detail-commence-date"
                    name="commence_date"
                    value={formState.commence_date || ''}
                    onChange={handleDateEvent}
                    format="YYYY-MM-DD"
                    disabled={saving}
                    placeholder="Select date"
                  />
                ) : (
                  <div className="hms-input-readonly">
                    {formatDate(contract.commence_date)}
                  </div>
                )}
              </Field>

              <Field
                label="Contract Period"
                hint={isEditing ? 'Optional — leave blank for open-ended' : undefined}
              >
                {isEditing ? (
                  <DateRangePicker
                    startDateName="period_start"
                    endDateName="period_end"
                    startDateValue={formState.period_start || ''}
                    endDateValue={formState.period_end || ''}
                    onChange={handleDateEvent}
                    placeholder="Select period…"
                    disabled={saving}
                  />
                ) : (
                  <div className="hms-input-readonly">
                    {formatPeriod(contract.period_start, contract.period_end)}
                  </div>
                )}
              </Field>
            </div>

            <Field
              label="Term Notes"
              hint={isEditing ? 'Free-form clauses or remarks' : 'Captured at contract creation'}
            >
              {isEditing ? (
                <textarea
                  value={formState.term_notes || ''}
                  onChange={(e) => handleFieldChange('term_notes', e.target.value)}
                  disabled={saving}
                  rows={4}
                  placeholder="e.g. FOB Singapore, payment 30 days net…"
                />
              ) : (
                <div className="hms-input-readonly legal-detail-term-notes">
                  {contract.term_notes || '—'}
                </div>
              )}
            </Field>
          </Section>

          <Section title="Status and Legal review">
            <div className="hms-grid hms-grid-2">
              {contract.can_change_status ? (
                <Field label="Contract status">
                  <select
                    id="legal-detail-status"
                    aria-label="Contract status"
                    value={contract.status || 'in_progress'}
                    onChange={(e) => review.changeStatus(e.target.value)}
                    disabled={review.busy === 'status'}
                  >
                    {CONTRACT_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Contract status">
                  <div className="hms-input-readonly">{contract.status_display || '—'}</div>
                </Field>
              )}
              <Field label="Legal review">
                <div className="hms-input-readonly">{contract.legal_review_status_display || '—'}</div>
              </Field>
              {contract.sent_to_legal_at && (
                <Field label="Sent to Legal">
                  <div className="hms-input-readonly">
                    {byLine(contract.sent_to_legal_by_name, contract.sent_to_legal_at)}
                  </div>
                </Field>
              )}
              {contract.reviewed_at && (
                <Field label="Reviewed by Legal">
                  <div className="hms-input-readonly">
                    {byLine(contract.reviewed_by_name, contract.reviewed_at)}
                  </div>
                </Field>
              )}
            </div>
            {contract.review_note && (
              <Field label="Note from Legal">
                <div className="hms-input-readonly legal-detail-term-notes">{contract.review_note}</div>
              </Field>
            )}
            {contract.can_review && (
              <Field label="Note for the sender" hint="Optional">
                <textarea
                  aria-label="Note for the sender"
                  value={review.note}
                  onChange={(e) => review.setNote(e.target.value)}
                  disabled={review.busy === 'review'}
                  rows={3}
                />
              </Field>
            )}
            {(contract.can_review
              || (contract.can_change_status && contract.legal_review_status !== 'pending')) && (
              <div className="legal-detail-review-actions">
                {contract.can_change_status && contract.legal_review_status !== 'pending' && (
                  <HMSButton
                    variant="secondary"
                    onClick={review.sendToLegal}
                    disabled={review.busy === 'send'}
                  >
                    {review.busy === 'send' ? 'Sending...' : 'Send to Legal'}
                  </HMSButton>
                )}
                {contract.can_review && (
                  <HMSButton
                    variant="primary"
                    onClick={review.markReviewed}
                    disabled={review.busy === 'review'}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    {review.busy === 'review' ? 'Saving...' : 'Mark reviewed'}
                  </HMSButton>
                )}
              </div>
            )}
          </Section>

          <Section title="Expiry and reminders">
            <div className="hms-grid hms-grid-2">
              {expiry.editing ? (
                // The tick sits outside the Field: Field's text-box input
                // rule would draw it as a full-width bar (as in AddContractModal).
                <div>
                  <Field label="Expiry date">
                    <DatePicker
                      id="legal-detail-expiry-date"
                      name="expiry_date"
                      value={expiry.expiryDate}
                      onChange={(e) => expiry.setExpiryDate(e.target.value)}
                      format="YYYY-MM-DD"
                      disabled={expiry.busy === 'expiry' || expiry.noExpiry}
                      placeholder="Select date"
                    />
                  </Field>
                  <label className="legal-detail-check">
                    <input
                      type="checkbox"
                      checked={expiry.noExpiry}
                      onChange={(e) => expiry.setNoExpiry(e.target.checked)}
                      disabled={expiry.busy === 'expiry'}
                    />
                    <span>No expiry / rolls over</span>
                  </label>
                </div>
              ) : (
                <Field label="Expires">
                  <div className="hms-input-readonly">
                    {expiryText(contract) || '—'}
                    {expiryFlag(contract) && contract.expiry_state !== 'rolls_over' && (
                      <HMSBadge
                        status={expiryFlag(contract).tone === 'danger' ? 'overdue' : 'pending'}
                        className="legal-detail-expiry-flag"
                      >
                        {expiryFlag(contract).label}
                      </HMSBadge>
                    )}
                  </div>
                </Field>
              )}
              <Field label="Reminders before expiry">
                <div className="hms-input-readonly">
                  {contract.expiry_steps && contract.expiry_steps.length ? (
                    <ul className="legal-detail-steps">
                      {contract.expiry_steps.map((step) => (
                        <li key={step.days_before}>{stepLine(step)}</li>
                      ))}
                    </ul>
                  ) : noStepsText(contract)}
                </div>
              </Field>
            </div>
            {contract.can_change_expiry && (
              <div className="legal-detail-review-actions">
                {expiry.editing ? (
                  <>
                    <HMSButton
                      variant="secondary"
                      onClick={() => expiry.setEditing(false)}
                      disabled={expiry.busy === 'expiry'}
                    >
                      Cancel
                    </HMSButton>
                    <HMSButton variant="primary" onClick={expiry.saveExpiry} disabled={expiry.busy === 'expiry'}>
                      {expiry.busy === 'expiry' ? 'Saving...' : 'Save expiry'}
                    </HMSButton>
                  </>
                ) : (
                  <HMSButton variant="secondary" onClick={expiry.startEdit}>
                    Change expiry
                  </HMSButton>
                )}
              </div>
            )}

            <Field label="Your reminders">
              <div className="hms-input-readonly">
                {contract.my_reminders && contract.my_reminders.length ? (
                  <ul className="legal-detail-steps">
                    {contract.my_reminders.map((r) => (
                      <li key={r.id}>
                        {formatDay(r.remind_on)}: {r.note}
                        {r.sent_at ? ' (sent)' : (
                          <button
                            type="button"
                            className="legal-detail-link-button"
                            onClick={() => expiry.removeReminder(r)}
                            disabled={expiry.busy === r.id}
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : 'None set.'}
              </div>
            </Field>
            <div className="hms-grid hms-grid-2">
              <Field label="Remind me on">
                <DatePicker
                  id="legal-detail-remind-on"
                  name="remind_on"
                  value={expiry.remindOn}
                  onChange={(e) => expiry.setRemindOn(e.target.value)}
                  format="YYYY-MM-DD"
                  disabled={expiry.busy === 'reminder'}
                  placeholder="Select date"
                />
              </Field>
              <Field label="Note" hint="What should the reminder say?">
                <input
                  type="text"
                  aria-label="Note"
                  value={expiry.note}
                  onChange={(e) => expiry.setNote(e.target.value)}
                  disabled={expiry.busy === 'reminder'}
                  maxLength={1000}
                />
              </Field>
            </div>
            <div className="legal-detail-review-actions">
              <HMSButton
                variant="secondary"
                onClick={expiry.addReminder}
                disabled={expiry.busy === 'reminder' || !expiry.remindOn || !expiry.note.trim()}
              >
                {expiry.busy === 'reminder' ? 'Setting...' : 'Set reminder'}
              </HMSButton>
            </div>
          </Section>

          <Section title="Audit" stepNumber={2}>
            <div className="hms-grid hms-grid-2">
              <Field label="Created by">
                <div className="hms-input-readonly">
                  {contract.created_by_name || '—'}
                </div>
              </Field>
              <Field label="Created at">
                <div className="hms-input-readonly">
                  {formatDateTime(contract.created_at)}
                </div>
              </Field>
              <Field label={contract.commodity_type === 'freight' ? 'Source Freight Recap' : 'Source Enquiry'}>
                <div className="hms-input-readonly">
                  {contract.commodity_type === 'freight'
                    ? (contract.freight_recap_number || '—')
                    : (contract.source_fuel_enquiry || '—')}
                </div>
              </Field>
              <Field label="Linked Recap">
                <div className="hms-input-readonly">
                  {contract.trade_recap_number || contract.freight_recap_number || '—'}
                </div>
              </Field>
            </div>
          </Section>
        </div>

        <div className="legal-detail-grid__side">
          <Section title={`Files (${filesCount})`} stepNumber={3}>
            <ContractFileList
              ref={fileListRef}
              contract={contract}
              onContractChange={refreshContract}
              hideUploadButton
              canEdit={contract.can_edit}
            />
          </Section>
        </div>
      </div>

        <Footer />
      </div>
    </Layout>
  );
};

export default LegalContractDetail;

